import React, { useEffect, useMemo } from 'react';
import { 
  ShoppingCart, 
  Factory, 
  Coffee, 
  Truck, 
  FileQuestion,
  CheckCircle,
  CalendarDays,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePaymentConditions, calculateDueDates } from '@/hooks/usePaymentConditions';
import { format } from 'date-fns';
import { 
  type PurchaseNature, 
  PURCHASE_NATURE_LABELS, 
  PURCHASE_NATURE_DESCRIPTIONS,
  NATURE_MOVES_STOCK,
  getPurchaseNatureFromCfop,
} from '@/types/purchaseInvoice';
import type { WizardData } from './index';

interface Step3NatureProps {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
}

const NATURE_OPTIONS: { 
  id: PurchaseNature; 
  icon: React.ElementType; 
  movesStock: boolean;
}[] = [
  { id: 'revenda', icon: ShoppingCart, movesStock: true },
  { id: 'industrializacao', icon: Factory, movesStock: true },
  { id: 'uso_consumo', icon: Coffee, movesStock: false },
  { id: 'ativo_imobilizado', icon: Truck, movesStock: false },
  { id: 'outros', icon: FileQuestion, movesStock: false },
];

export function Step3Nature({ data, onUpdate }: Step3NatureProps) {
  const { conditions: paymentConditions } = usePaymentConditions();

  // Auto-suggest nature based on CFOP
  useEffect(() => {
    if (data.invoice.cfop_predominante) {
      const suggestedNature = getPurchaseNatureFromCfop(data.invoice.cfop_predominante);
      if (suggestedNature && data.nature !== suggestedNature) {
        onUpdate({ nature: suggestedNature });
      }
    }
  }, [data.invoice.cfop_predominante]);

  const handleNatureChange = (nature: PurchaseNature) => {
    onUpdate({ nature });
  };

  const handleFinancialChange = (checked: boolean) => {
    onUpdate({ generateFinancial: checked });
  };

  const suggestedNature = data.invoice.cfop_predominante 
    ? getPurchaseNatureFromCfop(data.invoice.cfop_predominante)
    : null;

  // Check if XML has duplicatas
  const xmlDuplicatas = Array.isArray(data.parsedXml?.cobr?.dup) ? data.parsedXml.cobr.dup : [];
  const hasXmlDuplicatas = xmlDuplicatas.length > 0;

  const totalValue = data.invoice.total_value || 0;

  // Calculate due dates from selected payment condition (Scenario B)
  const calculatedDueDates = useMemo(() => {
    if (hasXmlDuplicatas || !data.paymentConditionId) return [];
    const selectedCondition = paymentConditions.find(c => c.id === data.paymentConditionId);
    if (!selectedCondition) return [];
    const baseDate = data.invoice.emission_date ? new Date(data.invoice.emission_date) : new Date();
    return calculateDueDates(selectedCondition, baseDate);
  }, [data.paymentConditionId, paymentConditions, hasXmlDuplicatas, data.invoice.emission_date]);

  // Penny-perfect rounding: last installment absorbs remainder
  const pennyPerfectValues = useMemo(() => {
    const n = calculatedDueDates.length;
    if (n === 0) return [];
    const baseValue = Math.floor((totalValue / n) * 100) / 100;
    const remainder = Math.round((totalValue - baseValue * n) * 100) / 100;
    const values = Array(n).fill(baseValue);
    values[n - 1] = Math.round((values[n - 1] + remainder) * 100) / 100;
    return values;
  }, [totalValue, calculatedDueDates.length]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold">Natureza da Compra</h3>
        <p className="text-sm text-muted-foreground">
          Selecione o tipo de compra para classificação correta
        </p>
      </div>

      {/* Auto-suggestion alert */}
      {suggestedNature && (
        <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900">
          <CheckCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 dark:text-blue-200">
            <strong>Sugestão automática:</strong> Com base no CFOP {data.invoice.cfop_predominante}, 
            identificamos esta compra como <strong>{PURCHASE_NATURE_LABELS[suggestedNature]}</strong>.
          </AlertDescription>
        </Alert>
      )}

      {/* Stock Moving Options */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">
          Compras que movimentam estoque
        </h4>
        <div className="grid gap-3 md:grid-cols-2">
          {NATURE_OPTIONS.filter(opt => opt.movesStock).map((option) => {
            const Icon = option.icon;
            const isSelected = data.nature === option.id;
            
            return (
              <Card 
                key={option.id}
                className={`cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-primary ring-2 ring-primary/20' 
                    : 'hover:border-primary/50'
                }`}
                onClick={() => handleNatureChange(option.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{PURCHASE_NATURE_LABELS[option.id]}</span>
                        {suggestedNature === option.id && (
                          <Badge variant="secondary" className="text-xs">Sugerido</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {PURCHASE_NATURE_DESCRIPTIONS[option.id]}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                    }`}>
                      {isSelected && <CheckCircle className="h-3 w-3 text-primary-foreground" />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Non-Stock Moving Options */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-3">
          Compras que NÃO movimentam estoque
        </h4>
        <div className="grid gap-3 md:grid-cols-3">
          {NATURE_OPTIONS.filter(opt => !opt.movesStock).map((option) => {
            const Icon = option.icon;
            const isSelected = data.nature === option.id;
            
            return (
              <Card 
                key={option.id}
                className={`cursor-pointer transition-all ${
                  isSelected 
                    ? 'border-primary ring-2 ring-primary/20' 
                    : 'hover:border-primary/50'
                }`}
                onClick={() => handleNatureChange(option.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                    }`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{PURCHASE_NATURE_LABELS[option.id]}</span>
                        {suggestedNature === option.id && (
                          <Badge variant="secondary" className="text-xs">Sugerido</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {PURCHASE_NATURE_DESCRIPTIONS[option.id]}
                      </p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/30'
                    }`}>
                      {isSelected && <CheckCircle className="h-3 w-3 text-primary-foreground" />}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Financial Option */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="generateFinancial"
              checked={data.generateFinancial}
              onCheckedChange={(checked) => handleFinancialChange(!!checked)}
            />
            <div className="flex-1">
              <Label 
                htmlFor="generateFinancial" 
                className="font-medium cursor-pointer"
              >
                Gerar Contas a Pagar automaticamente
              </Label>
              <p className="text-xs text-muted-foreground">
                {hasXmlDuplicatas 
                  ? `${xmlDuplicatas.length} parcela(s) serão criadas com base nos dados da nota fiscal`
                  : 'Parcelas serão geradas com base na condição de pagamento selecionada'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Section - Only visible when generateFinancial is true */}
      {data.generateFinancial && (
        <Card className="border-blue-200 dark:border-blue-900">
          <CardContent className="p-4 space-y-4">
            {hasXmlDuplicatas ? (
              /* Scenario A: XML has duplicatas - readonly display */
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    <Label className="font-medium">
                      Parcelas identificadas na Nota: {xmlDuplicatas.length}x
                      {(() => {
                        const emissionDate = data.invoice.emission_date ? new Date(data.invoice.emission_date + 'T12:00:00') : null;
                        if (!emissionDate) return null;
                        const intervals = xmlDuplicatas
                          .filter((dup: any) => dup.dVenc)
                          .map((dup: any) => {
                            const dueDate = new Date(dup.dVenc + 'T12:00:00');
                            return Math.round((dueDate.getTime() - emissionDate.getTime()) / (1000 * 60 * 60 * 24));
                          })
                          .filter((d: number) => d > 0);
                        if (intervals.length === 0) return null;
                        return <span className="text-muted-foreground font-normal ml-1">({intervals.join('/')} dias)</span>;
                      })()}
                    </Label>
                  </div>
                  <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
                    Extraído do XML
                  </Badge>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parcela</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {xmlDuplicatas.map((dup: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {dup.nDup || `${index + 1}/${xmlDuplicatas.length}`}
                        </TableCell>
                        <TableCell>
                          {dup.dVenc 
                            ? format(new Date(dup.dVenc + 'T12:00:00'), 'dd/MM/yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(parseFloat(dup.vDup) || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : (
              /* Scenario B: No XML duplicatas - manual selection */
              <>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <Label className="font-medium">Configuração de Pagamento</Label>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wizardPaymentCondition">
                      Condição de Pagamento <span className="text-destructive">*</span>
                    </Label>
                    <Select 
                      value={data.paymentConditionId || ''} 
                      onValueChange={(v) => onUpdate({ paymentConditionId: v })}
                    >
                      <SelectTrigger id="wizardPaymentCondition">
                        <SelectValue placeholder="Selecione a condição" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentConditions.map((condition) => (
                          <SelectItem key={condition.id} value={condition.id}>
                            {condition.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Preview of calculated installments */}
                {calculatedDueDates.length > 0 && totalValue > 0 && (
                  <div className="bg-muted/50 rounded-lg p-3 mt-3">
                    <Label className="text-sm font-medium mb-2 block">
                      Preview das Parcelas ({calculatedDueDates.length}x)
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {calculatedDueDates.map((date, i) => (
                        <Badge key={i} variant="secondary" className="text-sm py-1.5 px-3">
                          <span className="font-semibold mr-1">{i + 1}ª -</span>
                          {format(date, 'dd/MM/yyyy')}
                          <span className="ml-1 text-muted-foreground">({formatCurrency(pennyPerfectValues[i] || 0)})</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {!data.paymentConditionId && (
                  <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900">
                    <AlertDescription className="text-amber-800 dark:text-amber-200 text-sm">
                      Selecione uma condição de pagamento para gerar as parcelas no Contas a Pagar.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <div className="bg-muted/50 rounded-lg p-4">
        <h4 className="font-medium mb-2">Resumo da Classificação</h4>
        <div className="grid gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Natureza:</span>
            <span className="font-medium">{PURCHASE_NATURE_LABELS[data.nature]}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Movimenta Estoque:</span>
            <span className={`font-medium ${NATURE_MOVES_STOCK[data.nature] ? 'text-green-600' : 'text-muted-foreground'}`}>
              {NATURE_MOVES_STOCK[data.nature] ? 'Sim' : 'Não'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Gerar Financeiro:</span>
            <span className={`font-medium ${data.generateFinancial ? 'text-green-600' : 'text-muted-foreground'}`}>
              {data.generateFinancial 
                ? hasXmlDuplicatas 
                  ? `Sim (${xmlDuplicatas.length} parcela${xmlDuplicatas.length > 1 ? 's' : ''} do XML)`
                  : calculatedDueDates.length > 0
                    ? `Sim (${calculatedDueDates.length} parcela${calculatedDueDates.length > 1 ? 's' : ''})`
                    : 'Sim'
                : 'Não'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
